import React from 'react';
import { Eye, LogOut, Crown, User, Save, FolderOpen } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import MassMasterLogo from './MassMasterLogo';

const Header = ({ visitCount, onQuickSave, onOpenProjects, lastSaveTime }) => {
  const currentYear = new Date().getFullYear();
  const { user, subscription, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatLastSave = () => {
    if (!lastSaveTime) return null;
    const date = new Date(lastSaveTime);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };
  
  return (
    <header className="bg-card border-b border-border shadow-sm">
      <div className="container mx-auto px-4 py-4 max-w-7xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Custom unique logo - same as all other pages */}
            <MassMasterLogo size="sm" />
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-1">
                Mass Master Pro
                <sup className="text-[10px] font-normal text-muted-foreground">™</sup>
              </h1>
              <p className="text-xs text-muted-foreground">
                Professional Material Calculator & Estimator
                <span className="mx-1.5">•</span>
                <span className="text-[10px]">© {currentYear} All Rights Reserved</span>
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Save/Load Buttons */}
            {onQuickSave && (
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onQuickSave}
                  className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                >
                  <Save className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Save</span>
                </Button>
                {onOpenProjects && (
                  <Button variant="outline" size="sm" onClick={onOpenProjects}>
                    <FolderOpen className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Projects</span>
                  </Button>
                )}
                {lastSaveTime && (
                  <span className="hidden md:inline text-xs text-muted-foreground">
                    Saved {formatLastSave()}
                  </span>
                )}
              </div>
            )}

            {/* Visit Counter */}
            {visitCount !== null && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-full border border-border">
                <Eye className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">{visitCount.toLocaleString()}</span>
                <span className="text-xs text-muted-foreground">visits</span>
              </div>
            )}

            {/* Subscription Badge */}
            {subscription && (
              <div className={`hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                subscription.subscription_status === 'active' 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : subscription.subscription_status === 'trialing'
                  ? 'bg-blue-100 text-blue-800 border border-blue-200'
                  : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
              }`}>
                <Crown className="w-3 h-3" />
                {subscription.subscription_status === 'active' ? 'Pro' : 
                 subscription.subscription_status === 'trialing' ? 'Trial' : 'Expired'}
              </div>
            )}

            {/* User Menu */}
            {user && (
              <div className="flex items-center gap-3">
                <div className="hidden sm:flex items-center gap-2">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                  )}
                  <span className="text-sm font-medium text-foreground">{user.name?.split(' ')[0]}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground hover:text-foreground">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
